// PlayerHero — UIR-2 navy band with inline KPI row.
//
// DESIGN: "UI design refinement/HoopRoster Profile Redesign.dc.html" section 2a.
// Layout: navy band (#13213C hoop-nav) with decorative offset circle, 92px
// rounded avatar, name + availability chip (amber-outline), meta line
// (flag·country · position · team link · league), sync note (when api_sports id),
// right action stack (Contact agent primary / Save / Share small buttons),
// admin Update-stats inline, then KPI row at band bottom (PTS/g · REB/g · AST/g ·
// eFG% green when present · GP + season label — from most recent career_stats).
//
// Mobile: band stacks (avatar+name, meta wraps, actions full-width, KPI scrolls).

import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Bookmark, Share2, ArrowLeftRight, Check } from 'lucide-react'
import SyncBadge from './SyncBadge'
import Avatar from '../Avatar'
import { useCompare } from '../../context/CompareContext'

type AvailabilityStatus = 'open' | 'evaluating' | 'closed'

interface PlayerHeroPlayer {
  slug: string
  name: string
  jersey_number: number | null
  position: string | null
  nationality: string | null
  dob: string | null
  availability_status: AvailabilityStatus
  photo_url: string | null
  is_verified: boolean
  // Tier-1 content batch — manual 3x3 tag → small chip in the meta line,
  // right after the position segment.
  is_3x3_active: boolean
}

// UIR-2 — KPI row data comes from the parent (most recent career_stats season).
interface HeadlineStats {
  points_pg: number | null
  rebounds_pg: number | null
  assists_pg: number | null
  fg_pct: number | null // Used as eFG% in the hero row (mock shows FG as eFG%)
  games_played: number | null
  season: string | null
}

interface Props {
  player: PlayerHeroPlayer
  measurements: {
    height_cm: number | null
    weight_kg: number | null
    wingspan_cm: number | null
  } | null
  passportCountry: string | null
  awardsCount: number
  syncSource: string | null
  syncedAt: string | null
  actionAccessory?: ReactNode
  isSaved?: boolean
  onToggleSave?: () => void
  representationStatus?: 'represented' | 'pending' | 'unrepresented'
  currentTeam?: {
    team_name: string
    team_slug: string
    league_name: string
    league_slug: string
  } | null
  isAdmin?: boolean
  // PROMPT_COACH_SYNC — widened from isAdmin to any coach/agent.
  canSyncStats?: boolean
  onSyncStats?: () => void
  syncStatsInProgress?: boolean
  syncStatsResult?: string | null
  syncStatsError?: string | null
  // UIR-2 — inline KPI row (most recent season stats)
  headlineStats?: HeadlineStats | null
}

const AVAILABILITY: Record<
  AvailabilityStatus,
  { label: string; tone: 'green' | 'amber' | 'slate' }
> = {
  open: { label: 'Open to offers', tone: 'green' },
  evaluating: { label: 'Evaluating offers', tone: 'amber' },
  closed: { label: 'Not available', tone: 'slate' },
}

export default function PlayerHero({
  player,
  syncSource,
  syncedAt,
  actionAccessory,
  isSaved,
  onToggleSave,
  currentTeam,
  canSyncStats,
  onSyncStats,
  syncStatsInProgress,
  syncStatsResult,
  syncStatsError,
  headlineStats,
}: Props) {
  const availability = AVAILABILITY[player.availability_status]

  // UIR-2 — build meta line segments (flag+country · position · team · league)
  const metaSegments: string[] = []
  if (player.nationality) {
    metaSegments.push(player.nationality)
  }
  if (player.position) {
    metaSegments.push(player.position)
  }
  if (currentTeam) {
    metaSegments.push(currentTeam.team_name)
    metaSegments.push(currentTeam.league_name)
  }

  return (
    <header className="bg-hoop-nav dark:bg-hoop-nav text-hoop-bone dark:text-hoop-bone rounded-xl overflow-hidden relative">
      {/* Decorative offset circle */}
      <div className="absolute right-[-90px] top-[-90px] w-[300px] h-[300px] rounded-full border border-hoop-accent/10 pointer-events-none" />

      <div className="relative px-8 pt-7 pb-6">
        <div className="flex flex-col md:flex-row items-start gap-5">
          {/* 92px avatar */}
          <Avatar
            src={player.photo_url}
            name={player.name}
            className="w-[92px] h-[92px] rounded-xl flex-shrink-0"
            textClassName="text-3xl font-display font-semibold"
          />

          {/* Center: name + meta + sync */}
          <div className="flex-1 min-w-0">
            {/* Name + availability chip */}
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-[38px] font-display font-semibold leading-none tracking-tight">
                {player.name}
              </h1>
              {player.availability_status === 'evaluating' && (
                <span className="px-2 py-1 rounded text-[11.5px] font-semibold tracking-wide border bg-hoop-amber/10 text-hoop-amber border-hoop-amber/40">
                  {availability.label}
                </span>
              )}
              {player.availability_status === 'open' && (
                <span className="px-2 py-1 rounded text-[11.5px] font-semibold tracking-wide border bg-hoop-green/10 text-hoop-green border-hoop-green/40">
                  {availability.label}
                </span>
              )}
            </div>

            {/* Meta line */}
            {(metaSegments.length > 0 || player.is_3x3_active) && (
              <div className="mt-2.5 flex items-center gap-2.5 flex-wrap text-[14.5px] text-hoop-bone/70">
                {metaSegments.map((seg, i) => (
                  <span key={i} className="flex items-center gap-2.5">
                    {seg}
                    {i < metaSegments.length - 1 && (
                      <span className="text-hoop-slate-sep">·</span>
                    )}
                  </span>
                ))}
                {/* Tier-1 content batch — 3x3 chip near the position
                    segment. Chip (not a plain segment) so it reads as a
                    tag, matching the availability chips above. */}
                {player.is_3x3_active && (
                  <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold tracking-wide border bg-hoop-accent/10 text-hoop-accent border-hoop-accent/40">
                    3x3
                  </span>
                )}
              </div>
            )}

            {/* Sync note line (only when syncSource exists = api_sports id) */}
            {syncSource && syncedAt && (
              <div className="mt-2 text-xs text-hoop-bone/40">
                <SyncBadge source={syncSource} syncedAt={syncedAt} />
              </div>
            )}
          </div>

          {/* Right action stack */}
          <div className="flex flex-col gap-2 w-full md:w-[180px] shrink-0">
            {/* Primary: Contact agent / Express interest */}
            {actionAccessory && (
              <div className="w-full">{actionAccessory}</div>
            )}
            {/* PROMPT_COACH_SYNC — Update stats button for coach/agent/admin */}
            {canSyncStats && onSyncStats && (
              <button
                type="button"
                onClick={onSyncStats}
                disabled={syncStatsInProgress}
                className="w-full px-4 py-2.5 text-[13.5px] font-semibold rounded-lg bg-hoop-accent text-hoop-primary-fg hover:opacity-90 disabled:opacity-40"
              >
                {syncStatsInProgress ? 'Updating…' : 'Update stats'}
              </button>
            )}
            {syncStatsResult && (
              <span className="text-[11px] text-hoop-green">{syncStatsResult}</span>
            )}
            {syncStatsError && (
              <div className="text-[11px] text-hoop-red">
                {syncStatsError}
                {/* STRIPE_SPEC Phase 2 — upgrade link when cap is hit */}
                {syncStatsError.includes('Daily update limit reached') && (
                  <>
                    {' — '}
                    <Link to="/pricing" className="underline hover:text-hoop-red-dark">
                      Upgrade to Coach Pro
                    </Link>
                  </>
                )}
              </div>
            )}
            {/* Small buttons: Save + Compare + Share — icon-only with
              * tooltips (UX Issue 1, Kaillum 07-16): three icon+text
              * buttons clipped inside the 180px desktop stack. */}
            <div className="flex gap-3">
              {isSaved !== undefined && onToggleSave && (
                <button
                  type="button"
                  onClick={onToggleSave}
                  title={isSaved ? 'Unsave player' : 'Save player'}
                  aria-label={isSaved ? 'Unsave player' : 'Save player'}
                  className="flex-1 flex items-center justify-center px-3 py-2.5 rounded-lg border border-hoop-slate-border text-hoop-bone hover:bg-hoop-slate-border/20"
                >
                  <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                </button>
              )}
              <CompareButton slug={player.slug} />
              <ShareButton />
            </div>
          </div>
        </div>

        {/* Inline KPI row at band bottom */}
        {headlineStats && (
          <div className="mt-6 pt-5 border-t border-hoop-accent/20 flex gap-8 items-baseline overflow-x-auto scrollbar-none">
            <KPI
              value={headlineStats.points_pg}
              label="PTS/g"
            />
            <KPI
              value={headlineStats.rebounds_pg}
              label="REB/g"
            />
            <KPI
              value={headlineStats.assists_pg}
              label="AST/g"
            />
            <KPI
              value={headlineStats.fg_pct}
              label="eFG%"
              isPct
              greenWhenPresent
            />
            <KPI
              value={headlineStats.games_played}
              label={`GP${headlineStats.season ? ` · ${headlineStats.season}` : ''}`}
            />
          </div>
        )}
      </div>
    </header>
  )
}

function KPI({
  value,
  label,
  isPct = false,
  greenWhenPresent = false,
}: {
  value: number | null
  label: string
  isPct?: boolean
  greenWhenPresent?: boolean
}) {
  const displayValue = value == null
    ? '—'
    : isPct
    ? `${(value * 100).toFixed(1)}%`
    : Number.isInteger(value)
    ? String(value)
    : value.toFixed(1)

  const valueColor =
    greenWhenPresent && value != null
      ? 'text-hoop-green'
      : 'text-hoop-bone'

  return (
    <div className="flex-shrink-0">
      <span className={`font-stat font-bold text-2xl ${valueColor}`}>
        {displayValue}
      </span>
      <span className="ml-2 text-[11px] uppercase tracking-widest text-hoop-accent">
        {label}
      </span>
    </div>
  )
}

function CompareButton({ slug }: { slug: string }) {
  const compare = useCompare()
  const [feedback, setFeedback] = useState('')
  const inCompare = compare.has(slug)

  function handleClick() {
    if (inCompare) {
      compare.remove(slug)
      setFeedback('Removed from compare')
    } else {
      const result = compare.add(slug)
      if (result.success) {
        setFeedback('Added to compare')
      } else {
        setFeedback(result.reason ?? 'Failed to add')
      }
    }
    window.setTimeout(() => setFeedback(''), 2000)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title={feedback || (inCompare ? 'Remove from compare' : 'Add to compare')}
      aria-label={inCompare ? 'Remove from compare' : 'Add to compare'}
      className={`flex-1 flex items-center justify-center px-3 py-2.5 rounded-lg border ${
        inCompare
          ? 'border-hoop-accent bg-hoop-accent text-hoop-primary-fg'
          : 'border-hoop-slate-border text-hoop-bone hover:bg-hoop-slate-border/20'
      }`}
    >
      <ArrowLeftRight className="w-4 h-4" />
    </button>
  )
}

function ShareButton() {
  const [copied, setCopied] = useState(false)

  async function share() {
    if (typeof window === 'undefined') return
    try {
      // Use native share sheet if available (mobile)
      if (navigator.share) {
        await navigator.share({
          title: document.title,
          url: window.location.href,
        })
      } else {
        // Fallback to clipboard copy
        await navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      }
    } catch (err) {
      // Ignore AbortError (user dismissed share sheet) and other errors
    }
  }

  return (
    <button
      type="button"
      onClick={share}
      title={copied ? 'Link copied' : 'Share profile'}
      aria-label="Share profile"
      className="flex-1 flex items-center justify-center px-3 py-2.5 rounded-lg border border-hoop-slate-border text-hoop-bone hover:bg-hoop-slate-border/20"
    >
      {copied ? (
        <Check className="w-4 h-4 text-hoop-green" />
      ) : (
        <Share2 className="w-4 h-4" />
      )}
    </button>
  )
}
